import { BASE, getCsrfToken } from "./constants";

import type {
  PaymentMethod,
  SubscriptionSettingsUpdate,
} from "./subscription";

const { origin } = window.location;

const PAYMENT_OPTIONS = {
  yookassa: {
    payment_method_type: "bank_card",
    return_url: `${origin}/subscription/?scenario=creation&payment-attempt-status=unknown`,
  },
  stripe: {
    success_url: `${origin}/subscription/?scenario=creation&payment-attempt-status=success`,
    cancel_url: `${origin}/subscription/?scenario=creation&payment-attempt-status=failed`,
  },
};

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
}

export interface AutopaymentError {
  code: string;
  description: string;
}

export interface AutopaymentStatus {
  status: "active" | "failed" | "canceled" | "pending" | null;
  latest_error: AutopaymentError | null;
}

const createAutopayment = async ({
  provider,
  plan,
  interval,
  userData,
  coupon,
  trial,
  settings,
}: {
  provider: string;
  plan: string;
  interval: string;
  userData: UserData;
  coupon?: string;
  trial?: string;
  settings?: SubscriptionSettingsUpdate;
}): Promise<{ redirect_url: string }> => {
  const res = await fetch("/api/autopayment/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({
      provider,
      plan,
      interval,
      coupon: coupon || undefined,
      trial: trial || undefined,
      settings,
      user_data: {
        first_name: userData.firstName,
        second_name: userData.lastName,
        email: userData.email,
        country: userData.country,
      },
      options: PAYMENT_OPTIONS,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const updateAutopayment = async ({
  plan,
  interval,
  userData,
  coupon,
  settings,
}: {
  plan: string;
  interval: string;
  userData: UserData;
  coupon?: string;
  settings?: SubscriptionSettingsUpdate;
}): Promise<void> => {
  const res = await fetch("/api/autopayment/", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({
      plan,
      interval,
      coupon: coupon || undefined,
      settings,
      user_data: {
        first_name: userData.firstName,
        second_name: userData.lastName,
        email: userData.email,
        country: userData.country,
      },
      options: PAYMENT_OPTIONS,
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

const deleteAutopayment = async (): Promise<void> => {
  const res = await fetch("/api/autopayment/", {
    method: "DELETE",
    headers: { "X-CSRFToken": getCsrfToken() },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

const getAutopayment = async (): Promise<AutopaymentStatus | null> => {
  const res = await fetch("/api/autopayment/");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.autopayment ?? data;
};

const createPaymentMethod = async (): Promise<{ redirect_url: string }> => {
  const res = await fetch("/api/payment-methods/", {
    method: "POST",
    headers: { "X-CSRFToken": getCsrfToken() },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const getPaymentMethod = async (): Promise<PaymentMethod | null> => {
  const res = await fetch("/api/payment-methods/current");
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.payment_method;
};

const updateAutopaymentStatus = async (value: "active"): Promise<void> => {
  const res = await fetch("/api/autopayment/status/", {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": getCsrfToken(),
    },
    body: JSON.stringify({ value }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

const requestInvoice = async ({
  selectedPlan,
  annualPayment,
  company_name,
  contact_person,
  contact_email,
  contact_phone,
  edo_operator,
  comment,
  files,
  settings,
}: {
  selectedPlan: string;
  annualPayment: string;
  company_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone: string;
  edo_operator: string;
  comment: string;
  files?: File[];
  settings?: SubscriptionSettingsUpdate;
}): Promise<void> => {
  const formData = new FormData();
  const entries: Record<string, string> = {
    "selected-plan": selectedPlan,
    "annual-payment": annualPayment,
    ngw_plans_period: annualPayment === "on" ? "12" : "1",
    "customer-type": "entity",
    payment_gateway: "invoice",
    payment_type: "invoice",
    "form_entity-company_name": company_name,
    "form_entity-contact_person": contact_person,
    "form_entity-contact_email": contact_email,
    "form_entity-contact_phone": contact_phone,
    "form_entity-edo_operator": edo_operator,
    "form_entity-comment": comment,
  };
  Object.entries(entries).forEach(([k, v]) => formData.append(k, v));
  if (settings) formData.append("settings", JSON.stringify(settings));

  if (files && files.length > 0) {
    files.forEach((file) => {
      formData.append("files", file); 
    });
  }

  const res = await fetch(`${BASE}/subscription/invoice/`, {
    method: "POST",
    headers: { "X-CSRFTOKEN": getCsrfToken() },
    body: formData,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

export {
  createAutopayment,
  createPaymentMethod,
  deleteAutopayment,
  getAutopayment,
  getPaymentMethod,
  requestInvoice,
  updateAutopayment,
  updateAutopaymentStatus,
};
